import { ErrorMessage } from "@hookform/error-message";
import i18next from "i18next";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Select, { components, MenuListProps } from "react-select";
import { useInfiniteTags } from "../api/hooks/useInfiniteTags";
import {
    useAllCollaborators,
    useCategories,
    useGetCurrentUserTags,
    usePostCreateRisk,
    useSubcategories
} from "../api/hooks/useRiskThemes";
import { useAdmin } from "../context/adminContext";
import { useDesignSystem } from "../context/designSystemContext";
import { customMultiSelectStyles, customStyles } from "../styles/MultiSelectStyles";
import { Tag } from "../types/tag";
import { showErrorToast } from "../utils/errorToast";
import { pSBC } from "../utils/pSBC";
import { mergeTagOptions, TagOption } from "../utils/tagSelectOptions";

export const RegisterCategories = () => {
  useTranslation();
  const isAdmin = useAdmin();
  const isAdminUser = Boolean(isAdmin);
  const navigate = useNavigate(); // usado para redirecionar pagina
  const [tagsSearch, setTagsSearch] = useState("");
  const severityOptions = [
    i18next.t("severity_options_low"),
    i18next.t("severity_options_medium"),
    i18next.t("severity_options_high"),
  ];
  const probabilityOptions = [
    i18next.t("probability_options_low"),
    i18next.t("probability_options_medium"),
    i18next.t("probability_options_high"),
  ];

  const {
    control,
    register,
    handleSubmit,
    reset,
    trigger,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: "",
      category: "",
      subcategory: "",
      theme: "",
      severity: "",
      probability: "",
      risk_owner: "",
      risk_managers: [],
      description: "",
      year: "",
      risk_tags: [],
    },
  });

  const { error, data: collaborators } = useAllCollaborators();
  const { data: categories } = useCategories();
  const { data: subcategories } = useSubcategories();
  const { data: currentUserTags, isLoading: currentUserTagsLoading } =
    useGetCurrentUserTags();
  const {
    tags: infiniteTags,
    isLoading: infiniteTagsLoading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useInfiniteTags({ enabled: isAdminUser, search: tagsSearch, pageSize: 20 });
  const { designSystem } = useDesignSystem();

  const availableTags: Tag[] = isAdminUser
    ? infiniteTags ?? []
    : currentUserTags?.adminTags ?? [];

  const fetchNextTagsPage = () => {
    if (!isAdminUser) {
      return;
    }

    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  const InfiniteMenuList = (props: MenuListProps<TagOption, true>) => {
    const { innerProps } = props;

    return (
      <components.MenuList
        {...props}
        innerProps={{
          ...innerProps,
          onScroll: (event) => {
            innerProps?.onScroll?.(event);

            const target = event.currentTarget;
            const reachedBottom =
              target.scrollHeight - target.scrollTop - target.clientHeight <= 2;

            if (reachedBottom) {
              fetchNextTagsPage();
            }
          },
        }}
      />
    );
  };

  const translateSeverity2Backend = (severity: string) => {
    switch (severity) {
      case i18next.t("severity_options_low"):
        return "LOW";
      case i18next.t("severity_options_medium"):
        return "REGULAR";
      case i18next.t("severity_options_high"):
        return "HIGH";
    }
    return severity;
  };

  const translateProbability2Backend = (probability: string) => {
    switch (probability) {
      case i18next.t("probability_options_low"):
        return "LOW";
      case i18next.t("probability_options_medium"):
        return "REGULAR";
      case i18next.t("probability_options_high"):
        return "HIGH";
    }
  };

  const { mutate: createRisk } = usePostCreateRisk();

  const onSubmit = (data: any) => {
    const request = {
      category: data.category,
      subcategory: data.subcategory,
      theme: data.theme,
      year: data.year,
      name: data.name,
      description: data.description,
      probability: translateProbability2Backend(data.probability),
      severity: translateSeverity2Backend(data.severity),
      ownerId: data.risk_owner.value,
      managersIds: data.risk_managers?.map((item: any) => item.value) || [],
      tags: data.risk_tags?.map((item: any) => item.value) || [],
    };
    createRisk(request, {
      onSuccess: () => {
        reset();
        navigate("/");
      },
      onError: error => {
        console.log("Erro ao cadastrar risco:", error);
        showErrorToast(error, i18next.t("add_risk_error"));
      },
    });
  };

  if (error) return <div>Error: {error.message}</div>;

  const tailwindDiv = "";
  const tailwindLabels =
    "text-[#191919] text-[12px] leading-[12px] tracking-[0.1px] font-openSans font-bold mb-2";
  const tailwindInputs =
    "w-full border border-gray-300 rounded-md py-1 pl-2 text-[#191919] text-[14px] tracking-[0.18px] font-openSans";
  const tailwindErrorMessage =
    "text-red-500 text-[12px] leading-[16px] tracking-[0.18px] absolute";

  const allCategories = categories?.map((val: string) => ({
    label: val,
    value: val,
  }));

  const allSubcategories = subcategories?.map((val: string) => ({
    label: val,
    value: val,
  }));

  const allCollaborators = collaborators?.map((val: any) => ({
    label: val.login
      .replace(/\b\w/g, (char: string) => char.toUpperCase())
      .split(".")
      .join(" "),
    value: val.id,
  }));

  return (
    <div className="container min-h-full mx-auto p-4 overflow-auto">
      <div className="flex justify-between items-center mx-3 mt-3 mb-4">
        <h1
          className="text-2xl font-bold"
          style={{
            color: `${pSBC(0.9, designSystem?.cssBasics.primaryColor)}`,
          }}
        >
          {i18next.t("add_risk")}
        </h1>
      </div>
      <div className="justify-center items-center m-24">
        <div className="flex flex-col max-w-4xl mx-auto">
          <h2
            className="text-[18px] font-semibold mb-4 pl-4 text-white p-2.5 rounded-md tracking-[0.29px]"
            style={{ backgroundColor: designSystem?.cssBasics.secondaryColor }}
          >
            {i18next.t("risk_details")}
          </h2>
        </div>

        <form
          className="bg-white p-5 rounded-[20px] shadow-custom-shadow max-w-4xl mx-auto"
          onSubmit={handleSubmit(onSubmit)}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className={tailwindDiv}>
              <label className={tailwindLabels}>{i18next.t("risk_name")}</label>
              <input
                className={tailwindInputs}
                type="text"
                placeholder="Nome"
                {...register("name", {
                  required: i18next.t("required_field"),
                  minLength: {
                    value: 4,
                    message: i18next.t("min_3_characters_field"),
                  },
                })}
              />
              <ErrorMessage
                name="name"
                errors={errors}
                render={({ message }) => (
                  <p className={tailwindErrorMessage}>{message}</p>
                )}
              />
            </div>

            <div className={tailwindDiv}>
              <label className={tailwindLabels}>{i18next.t("category")}</label>
              <Controller
                name="category"
                control={control}
                rules={{ required: i18next.t("required_field") }}
                render={({ field: { onChange, value } }) => (
                  <Select
                    options={allCategories}
                    placeholder={i18next.t("category")}
                    classNamePrefix="react-select"
                    isClearable={true}
                    isSearchable={true}
                    styles={customStyles}
                    onChange={selectedOption => {
                      onChange(selectedOption ? selectedOption.value : "");
                      trigger("category");
                    }}
                    value={
                      allCategories?.find(
                        (option: any) => option.value === value
                      ) || (value ? { value, label: value } : null)
                    }
                    onInputChange={(inputValue, { action }) => {
                      if (action === "input-change") {
                        onChange(inputValue);
                        trigger("category");
                      }
                    }}
                  />
                )}
              />

              <ErrorMessage
                name="category"
                errors={errors}
                render={({ message }) => (
                  <p className={tailwindErrorMessage}>{message}</p>
                )}
              />
            </div>

            <div className={tailwindDiv}>
              <label className={tailwindLabels}>
                {i18next.t("subcategory")}
              </label>
              <Controller
                name="subcategory"
                control={control}
                rules={{ required: i18next.t("required_field") }}
                render={({ field: { onChange, value, ref } }) => (
                  <Select
                    options={allSubcategories}
                    placeholder={i18next.t("subcategory")}
                    classNamePrefix="react-select"
                    isClearable={true}
                    isSearchable={true}
                    styles={customStyles}
                    onChange={selectedOption => {
                      onChange(selectedOption ? selectedOption.value : "");
                      trigger("subcategory");
                    }}
                    value={
                      allCategories?.find(
                        (option: any) => option.value === value
                      ) || (value ? { value, label: value } : null)
                    }
                    onInputChange={(inputValue, { action }) => {
                      if (action === "input-change") {
                        onChange(inputValue);
                        trigger("subcategory");
                      }
                    }}
                  />
                )}
              />
              <ErrorMessage
                name="subcategory"
                errors={errors}
                render={({ message }) => (
                  <p className={tailwindErrorMessage}>{message}</p>
                )}
              />
            </div>

            <div className={tailwindDiv}>
              <label className={tailwindLabels}>
                {i18next.t("risk_theme")}
              </label>
              <input
                className={tailwindInputs}
                type="text"
                placeholder={i18next.t("risk_theme")}
                {...register("theme", {
                  required: i18next.t("required_field"),
                })}
              />
              <ErrorMessage
                name="theme"
                errors={errors}
                render={({ message }) => (
                  <p className={tailwindErrorMessage}>{message}</p>
                )}
              />
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div className={tailwindDiv}>
                <label className={tailwindLabels}>
                  {i18next.t("severity")}
                </label>
                <select
                  className={tailwindInputs}
                  {...register("severity", {
                    required: i18next.t("required_field"),
                  })}
                >
                  {severityOptions.map((option, index) => (
                    <option key={index} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
                <ErrorMessage
                  name="severity"
                  errors={errors}
                  render={({ message }) => (
                    <p className={tailwindErrorMessage}>{message}</p>
                  )}
                />
              </div>

              <div className={tailwindDiv}>
                <label className={tailwindLabels}>
                  {i18next.t("probability")}
                </label>
                <select
                  className={tailwindInputs}
                  {...register("probability", {
                    required: i18next.t("required_field"),
                  })}
                >
                  {probabilityOptions.map((option, index) => (
                    <option key={index} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
                <ErrorMessage
                  name="probability"
                  errors={errors}
                  render={({ message }) => (
                    <p className={tailwindErrorMessage}>{message}</p>
                  )}
                />
              </div>

              <div className={tailwindDiv}>
                <label className={tailwindLabels}>{i18next.t("year")}</label>
                <input
                  className={tailwindInputs}
                  type="text"
                  placeholder={new Date().getFullYear().toString()}
                  {...register("year", {
                    required: i18next.t("required_field"),
                    pattern: {
                      value: /^\d{4}$/,
                      message: i18next.t("min_4_number_for_year"),
                    },
                  })}
                />
                <ErrorMessage
                  name="year"
                  errors={errors}
                  render={({ message }) => (
                    <p className={tailwindErrorMessage}>{message}</p>
                  )}
                />
              </div>
            </div>

            <div className={tailwindDiv}>
              <label className={tailwindLabels}>
                {i18next.t("risk_owner")}
              </label>
              <Controller
                name="risk_owner"
                control={control}
                rules={{ required: i18next.t("required_field") }}
                render={({ field: { onChange, value } }) => (
                  <Select
                    options={allCollaborators}
                    isClearable={true}
                    isSearchable={true}
                    styles={customStyles}
                    onChange={selectedOption => {
                      onChange(selectedOption ? selectedOption : "");
                      trigger("risk_owner");
                    }}
                    placeholder={i18next.t("risk_owner")}
                    classNamePrefix="react-select"
                    value={value || null}
                  />
                )}
              />

              <ErrorMessage
                name="risk_owner"
                errors={errors}
                render={({ message }) => (
                  <p className={tailwindErrorMessage}>{message}</p>
                )}
              />
            </div>

            <div className={tailwindDiv}>
              <label className={tailwindLabels}>
                {i18next.t("risk_managers")}
              </label>
              <Controller
                name="risk_managers"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <Select
                    options={allCollaborators}
                    isMulti
                    isClearable={true}
                    isSearchable={true}
                    closeMenuOnSelect={false}
                    hideSelectedOptions={false}
                    styles={customMultiSelectStyles}
                    onChange={(selectedOptions: any) => {
                      onChange(selectedOptions || []);
                      trigger("risk_managers");
                    }}
                    placeholder={i18next.t("risk_managers")}
                    classNamePrefix="react-select"
                    value={value || []}
                  />
                )}
              />

              <ErrorMessage
                name="risk_managers"
                errors={errors}
                render={({ message }) => (
                  <p className={tailwindErrorMessage}>{message}</p>
                )}
              />
            </div>

            <div className={tailwindDiv}>
              <label className={tailwindLabels}>
                {i18next.t("risk_tags")}
              </label>
              <Controller
                name="risk_tags"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <Select<TagOption, true>
                    options={mergeTagOptions({
                      loadedTags: availableTags,
                      selected: (value || []) as TagOption[],
                    })}
                    isMulti
                    isClearable={true}
                    isSearchable={true}
                    closeMenuOnSelect={false}
                    hideSelectedOptions={false}
                    components={{ MenuList: InfiniteMenuList }}
                    styles={customMultiSelectStyles}
                    onChange={(selectedOptions: any) =>
                      onChange(selectedOptions || [])
                    }
                    onInputChange={(inputValue, meta) => {
                      if (isAdminUser && meta.action === "input-change") {
                        setTagsSearch(inputValue);
                      }

                      return inputValue;
                    }}
                    onMenuScrollToBottom={fetchNextTagsPage}
                    isLoading={
                      (isAdminUser && (infiniteTagsLoading || isFetchingNextPage)) ||
                      (!isAdminUser && currentUserTagsLoading)
                    }
                    placeholder={i18next.t("risk_tags_placeholder")}
                    classNamePrefix="react-select"
                    value={value || []}
                  />
                )}
              />
            </div>

            <div className="col-span-2">
              <label className={tailwindLabels}>
                {i18next.t("risk_description")}
              </label>
              <textarea
                className={tailwindInputs}
                rows={2}
                placeholder={i18next.t("risk_description")}
                {...register("description", {
                  required: i18next.t("required_field"),
                })}
              />
              <ErrorMessage
                name="description"
                errors={errors}
                render={({ message }) => (
                  <p className={tailwindErrorMessage}>{message}</p>
                )}
              />
            </div>
          </div>

          <div className="grid grid-cols-2">
            <div className="flex mt-6">
              <button
                className="px-[70px]  text-white min-h-[33px] rounded font-semibold text-[14px] leading-[16.34px] text-center"
                style={{
                  backgroundColor: designSystem?.cssBasics.secondaryColor,
                }}
                type="submit"
              >
                {i18next.t("button_save")}
              </button>
            </div>
            <div className="flex justify-end mt-6">
              <button
                className="px-[70px] ml-4 min-h-[33px] rounded border-[2.33px] text-[14px] leading-[12px] tracking-[0.18px] font-bold py-2.5"
                style={{
                  color: designSystem?.cssBasics.secondaryColor,
                  borderColor: designSystem?.cssBasics.secondaryColor,
                }}
                type="button"
                onClick={() => navigate("/")}
              >
                {i18next.t("button_cancel")}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};
